// Note-creation entry points shared across the app: the "New Note" menu
// (record / import / YouTube), the hidden file input and the global
// drag-and-drop overlay. Web equivalent of mobile's useCreateNoteActions.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
  createNoteFromFile,
  createNoteFromYoutube,
  parseYoutubeVideoId,
  FileTooLargeError,
} from '../services/createNote';
import {extensionOf} from '../services/storageService';
import {useAuth} from './AuthProvider';
import {useToast} from './ToastProvider';
import {useUpgrade} from './UpgradeProvider';
import {RecordModal} from '../components/create/RecordModal';
import {YoutubeModal} from '../components/create/YoutubeModal';
import {Icon} from '../components/common/Icon';
import posthog, {isPostHogConfigured} from '../posthog';
import layoutCss from '../components/layout/layout.module.css';

const AUDIO_EXTENSIONS = ['m4a', 'mp3', 'wav', 'aac', 'mp4', 'webm', 'ogg'];
const ACCEPTED_EXTENSIONS = [...AUDIO_EXTENSIONS, 'pdf'];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.map(e => `.${e}`).join(',');

interface CreateContextValue {
  openRecord: () => void;
  openImport: () => void;
  openYoutube: () => void;
  /** Creates a note from a picked/dropped file (validates type + size). */
  importFile: (file: File) => Promise<void>;
}

const CreateContext = createContext<CreateContextValue>({
  openRecord: () => {},
  openImport: () => {},
  openYoutube: () => {},
  importFile: async () => {},
});

export function CreateProvider({children}: {children: ReactNode}) {
  const {uid} = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const {handleLimitError} = useUpgrade();
  const {t} = useTranslation();

  const [recordOpen, setRecordOpen] = useState(false);
  const [youtubeOpen, setYoutubeOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  const importFile = useCallback(
    async (file: File) => {
      if (!uid) {
        return;
      }
      const ext = extensionOf(file.name);
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        showToast(t('unsupported_file'), 'error');
        return;
      }
      try {
        const id = await createNoteFromFile(uid, file, ext);
        if (isPostHogConfigured) {
          posthog.capture('note_created', {source: 'file_upload', file_type: ext});
        }
        navigate(`/notes/${id}`);
      } catch (e) {
        if (e instanceof FileTooLargeError) {
          showToast(t('upload_error') + ' (max 200MB)', 'error');
        } else if (!handleLimitError(e)) {
          console.warn(e);
          showToast(t('upload_error'), 'error');
        }
      }
    },
    [uid, navigate, showToast, handleLimitError, t],
  );

  const submitRecording = useCallback(
    async (blob: Blob, ext: string, duration: number) => {
      if (!uid) {
        return;
      }
      setRecordOpen(false);
      try {
        const id = await createNoteFromFile(uid, blob, ext, {duration});
        if (isPostHogConfigured) {
          posthog.capture('note_created', {
            source: 'recording',
            file_type: ext,
            duration_seconds: Math.round(duration),
          });
        }
        navigate(`/notes/${id}`);
      } catch (e) {
        if (!handleLimitError(e)) {
          console.warn(e);
          showToast(t('upload_error'), 'error');
        }
      }
    },
    [uid, navigate, showToast, handleLimitError, t],
  );

  const submitYoutube = useCallback(
    async (url: string): Promise<boolean> => {
      const videoId = parseYoutubeVideoId(url);
      if (!videoId) {
        showToast(t('invalid_url'), 'error');
        return false;
      }
      try {
        const id = await createNoteFromYoutube(videoId);
        if (isPostHogConfigured) {
          posthog.capture('note_created', {source: 'youtube'});
        }
        setYoutubeOpen(false);
        navigate(`/notes/${id}`);
        return true;
      } catch (e) {
        if (!handleLimitError(e)) {
          console.warn(e);
          showToast(t('ErrorFetch'), 'error');
        }
        return false;
      }
    },
    [navigate, showToast, handleLimitError, t],
  );

  // Global drag-and-drop.
  useEffect(() => {
    const onDragEnter = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) {
        dragDepth.current++;
        setDragging(true);
      }
    };
    const onDragLeave = () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) {
        setDragging(false);
      }
    };
    const onDragOver = (e: DragEvent) => e.preventDefault();
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) {
        void importFile(file);
      }
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [importFile]);

  // Keyboard shortcut: N opens the recorder (when not typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;
      if (typing || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setRecordOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <CreateContext.Provider
      value={{
        openRecord: () => setRecordOpen(true),
        openImport: () => fileInputRef.current?.click(),
        openYoutube: () => setYoutubeOpen(true),
        importFile,
      }}
    >
      {children}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        style={{display: 'none'}}
        onChange={e => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) {
            void importFile(file);
          }
        }}
      />
      {dragging && (
        <div className={layoutCss.dropOverlay}>
          <div className={layoutCss.dropInner}>
            <Icon name="upload" size={40} />
            {t('WebDropFiles')}
          </div>
        </div>
      )}
      <RecordModal
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        onSubmit={submitRecording}
      />
      <YoutubeModal
        open={youtubeOpen}
        onClose={() => setYoutubeOpen(false)}
        onSubmit={submitYoutube}
      />
    </CreateContext.Provider>
  );
}

export const useCreate = () => useContext(CreateContext);
